import { EffectsCommandMap } from 'dva';
import { ConnectState } from '@/models/connect';
import { FI18n, FServiceAPI } from '@freelog/tools-lib';
import fMessage from '@/components/fMessage';
import {
  handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2,
} from '@/utils/service';
import { FRegExpMgr } from '@/utils/FRegExpMgr';
import type {
  ChangeAction,
  OnChange_step1_resourceType_Action,
  OnChange_step1_resourceTitle_Action,
  OnChange_step1_resourceName_Action,
  OnVerify_step1_resourceName_Action,
  OnClick_step1_createBtn_Action,
} from './types';

export const step1Effects = {
  *onChange_step1_resourceType(
    { payload }: OnChange_step1_resourceType_Action,
    { select, put }: EffectsCommandMap,
  ) {
    const { collectionCreatorPage }: ConnectState = yield select(
      ({ collectionCreatorPage }: ConnectState) => ({
        collectionCreatorPage,
      }),
    );
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step1_resourceType: payload.value,
        step1_dataIsDirty_count: collectionCreatorPage.step1_dataIsDirty_count + 1,
        step1_resourceType_errorText:
          payload.value === null
            ? FI18n.i18nNext.t('naming_convention_resource_type_required')
            : '',
      },
    });
  },
  *onChange_step1_resourceTitle(
    { payload }: OnChange_step1_resourceTitle_Action,
    { select, put }: EffectsCommandMap,
  ) {
    const { collectionCreatorPage }: ConnectState = yield select(
      ({ collectionCreatorPage }: ConnectState) => ({
        collectionCreatorPage,
      }),
    );
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step1_resourceTitle: payload.value,
        step1_resourceTitle_errorText:
          payload.value === ''
            ? FI18n.i18nNext.t('naming_convention_resource_title_required')
            : payload.value.length > 100
            ? '不超过100个字符'
            : '',
      },
    });
  },
  *onChange_step1_resourceName(
    { payload }: OnChange_step1_resourceName_Action,
    { select, put }: EffectsCommandMap,
  ) {
    const { collectionCreatorPage }: ConnectState = yield select(
      ({ collectionCreatorPage }: ConnectState) => ({
        collectionCreatorPage,
      }),
    );
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step1_resourceName: payload.value,
        step1_resourceName_optimized: FRegExpMgr.resourceNameOptimized(payload.value),
        step1_resourceName_errorText: '',
        step1_resourceName_isVerify: true,
        step1_dataIsDirty_count: collectionCreatorPage.step1_dataIsDirty_count + 1,
      },
    });
  },
  *onVerify_step1_resourceName(
    {}: OnVerify_step1_resourceName_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { collectionCreatorPage }: ConnectState = yield select(
      ({ collectionCreatorPage }: ConnectState) => ({
        collectionCreatorPage,
      }),
    );
    let nameErrorText: string = '';
    if (collectionCreatorPage.step1_resourceName_optimized === '') {
      nameErrorText = FI18n.i18nNext.t('naming_convention_resource_authid_required');
    } else {
      const params1 = {
        resourceIdOrName: encodeURIComponent(
          `${collectionCreatorPage.userInfo?.userName}/${collectionCreatorPage.step1_resourceName_optimized}`,
        ),
      };
      const { data: data_info } = yield call(FServiceAPI.Resource.info, params1);
      if (!!data_info) {
        nameErrorText = FI18n.i18nNext.t('resource_name_exist', {
          authID: collectionCreatorPage.step1_resourceName_optimized,
        });
      }
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step1_resourceName_isVerify: false,
        step1_resourceName_errorText: nameErrorText,
      },
    });
  },
  *onClick_step1_createBtn(
    {}: OnClick_step1_createBtn_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { collectionCreatorPage } = yield select(({ collectionCreatorPage }: ConnectState) => ({
      collectionCreatorPage,
    }));
    if (collectionCreatorPage.step1_resourceType === null) {
      return;
    }

    const params = {
      name: collectionCreatorPage.step1_resourceName_optimized,
      resourceTitle: collectionCreatorPage.step1_resourceTitle.trim(),
      resourceTypeCode: collectionCreatorPage.step1_resourceType.value,
      resourceTypeName: collectionCreatorPage.step1_resourceType.customInput || undefined,
      subjectType: 4,
    };
    const {
      ret,
      errCode,
      msg,
      data,
    }: {
      ret: number;
      errCode: number;
      msg: string;
      data: {
        resourceId: string;
        resourceName: string;
        resourceType: string[];
        resourceTypeCode: string;
      };
    } = yield call(FServiceAPI.Resource.create, params);
    if (ret !== 0 || errCode !== 0 || !data) {
      fMessage(msg, 'error');
      return;
    }

    const params1 = {
      code: collectionCreatorPage.step1_resourceType.value,
    };
    const {
      data: data_ResourceTypeInfo,
    }: {
      ret: number;
      errCode: number;
      msg: string;
      data: {
        resourceConfig: {
          fileCommitMode: number[];
          fileMaxSize: number;
          fileMaxSizeUnit: 1 | 2;
          supportDownload: 1 | 2;
          supportEdit: 1 | 2;
          supportOptionalConfig: 1 | 2;
        };
      };
    } = yield call(FServiceAPI.Resource.getResourceTypeInfoByCode, params1);
    const uploadEntry: import('./types').CollectionCreatorPageModelState['step2_resourceTypeConfig']['uploadEntry'] =
      [];
    if (data_ResourceTypeInfo.resourceConfig.fileCommitMode.includes(2 ** 0)) {
      uploadEntry.push('localUpload');
    }
    if (data_ResourceTypeInfo.resourceConfig.fileCommitMode.includes(2 ** 1)) {
      uploadEntry.push('storageSpace');
    }
    if (data_ResourceTypeInfo.resourceConfig.fileCommitMode.includes(2 ** 2)) {
      uploadEntry.push('markdownEditor');
    }
    if (data_ResourceTypeInfo.resourceConfig.fileCommitMode.includes(2 ** 3)) {
      uploadEntry.push('cartoonEditor');
    }
    if (data_ResourceTypeInfo.resourceConfig.fileCommitMode.includes(2 ** 4)) {
      uploadEntry.push('collectionLibrary');
    }
    if (data_ResourceTypeInfo.resourceConfig.fileCommitMode.includes(2 ** 5)) {
      uploadEntry.push('podcastRss');
    }

    const params4 = {
      sha1: '',
      resourceTypeCode: collectionCreatorPage.step1_resourceType.value,
      inheritData: {
        additionalProperties: [],
        customProperties: [],
        customConfigurations: [],
      },
    };
    const result = yield call(handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2, params4);

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step: 2,
        step1_createdResourceInfo: {
          resourceID: data.resourceId,
          resourceName: data.resourceName,
          resourceType: data.resourceType,
          resourceTypeCode: data.resourceTypeCode,
        },
        step1_dataIsDirty_count: 0,
        step2_resourceTypeConfig: {
          uploadEntry: uploadEntry,
          limitFileSize:
            data_ResourceTypeInfo.resourceConfig.fileMaxSize *
            1024 *
            1024 ** data_ResourceTypeInfo.resourceConfig.fileMaxSizeUnit,
          isSupportDownload: data_ResourceTypeInfo.resourceConfig.supportDownload === 2,
          isSupportEdit: data_ResourceTypeInfo.resourceConfig.supportEdit === 2,
          isSupportOptionalConfig:
            data_ResourceTypeInfo.resourceConfig.supportOptionalConfig === 2,
        },
        step2_systemProperties: result.systemProperties,
        step2_customProperties: result.customProperties,
        step2_customConfigurations: result.customConfigurations,
      },
    });
  },
};
